import { useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '../api/ApiError'
import { useCurrentUser, type CurrentUserDto } from './useCurrentUser'

export type LinkedAccountStatus = 'loading' | 'linked' | 'redirecting' | 'error'

/**
 * What the current-user read says about where a signed-in user belongs:
 *   - `noAccount`: no Viltrapport account at all (GET /api/v1/authentication is 404).
 *   - `noArea`: an account with ZERO district memberships. This happens for users in
 *     the shared /users collection (e.g. a WeHunt app user) who were never enrolled in
 *     a district: authentication returns 200 but every district-scoped endpoint 403s
 *     ("district.contextRequired").
 *   - `member`: at least one membership.
 *   - `unknown`: the read failed for another reason, so it says nothing either way.
 *
 * The first two belong on /welcome and a member never does. The shell sending users
 * to /welcome and the welcome pages sending members away both read this, so the two
 * cannot disagree.
 */
export type AccountStanding = 'pending' | 'noAccount' | 'noArea' | 'member' | 'unknown'

export function accountStanding(currentUser: {
  data?: CurrentUserDto
  error: unknown
  isError: boolean
}): AccountStanding {
  // A 404 wins over data from an earlier read: the account is gone now.
  if (currentUser.isError && currentUser.error instanceof ApiError && currentUser.error.status === 404) {
    return 'noAccount'
  }
  if (currentUser.data) return (currentUser.data.memberships?.length ?? 0) === 0 ? 'noArea' : 'member'
  return currentUser.isError ? 'unknown' : 'pending'
}

/** Whether a standing belongs on /welcome. */
export function belongsOnWelcome(standing: AccountStanding): boolean {
  return standing === 'noAccount' || standing === 'noArea'
}

// The app shell consumes this and sends users with nothing to show to /welcome
// rather than rendering the dashboard (which then fires district-scoped calls
// that all 403). /welcome lets them create an area (or wait to be added to a
// district).
export function useRequireLinkedAccount(): {
  status: LinkedAccountStatus
  /** The failed read, when status is `error`. */
  error: unknown
  /** Reads the account again. */
  retry: () => Promise<unknown>
} {
  const currentUser = useCurrentUser()
  const navigate = useNavigate()

  const standing = accountStanding(currentUser)
  const needsWelcome = belongsOnWelcome(standing)

  useEffect(() => {
    if (needsWelcome) {
      void navigate({ to: '/welcome', replace: true })
    }
  }, [needsWelcome, navigate])

  const status: LinkedAccountStatus =
    standing === 'member'
      ? 'linked'
      : needsWelcome
        ? 'redirecting'
        : // A failed read was reported as loading, and the query never refetches on
          // its own (staleTime: Infinity), so the shell stayed blank for good.
          standing === 'unknown'
          ? 'error'
          : 'loading'
  const { refetch } = currentUser
  return { status, error: currentUser.error, retry: useCallback(() => refetch(), [refetch]) }
}
